import {
  ComplianceDataType,
  PoliciesDataType,
  useComplianceReportService,
  INTERACTION_ACTIONS,
} from 'domain/shared/compliance';
import { ListResponse, LoadingParams, Filter } from 'framework/list';
import {
  PortalComplianceDataType,
  PortalComplianceGroupedEntry,
} from 'domain/people/employee/portal/compliance/List/types';
import { sortComplianceRegistryItemsForPortal } from 'domain/people/employee/compliance/shared/complianceRegistryPortalRules';
import { useEmployeeId } from '../employeeIdentityRouting';
import { usePeopleManagementApi } from 'infrastructure/api';
import { useNewProjectService } from 'domain/shared/newProject';
import {
  buildAssignmentReasonDictionaries,
  buildEmployeeAssignmentContext,
  buildGroupedDataByReason,
  loadProjectsForPortalReasons,
} from '../portal/compliance/List/shared/helpers';
import {
  PORTAL_COMPLIANCE_REGISTRY_ALL_ITEMS_PAGE_SIZE,
  useEmployeeService,
  usePositionService,
} from 'domain/shared/employee';
import useOfficeService from 'domain/shared/employee/officeService';

type ComplianceDocumentConsentPayload = {
  policyPath: string;
  isPolicyAccepted: boolean;
  consentPageUrl?: string;
};

const mapComplianceRegistryItemsToPortalData = (complianceItems: PoliciesDataType[]): PortalComplianceDataType[] =>
  complianceItems.map(complianceItem => ({
    ...complianceItem,
    portalRowId: `${complianceItem.id}-${complianceItem.policyPath}`,
    isViewed:
      complianceItem?.interactionTimeline?.some(
        item => item?.action === INTERACTION_ACTIONS.VIEWED && item?.policyPath === complianceItem?.policyPath,
      ) ?? false,
  }));

const transformPortalComplianceRegistry = (complianceItems: PoliciesDataType[], requestParams: LoadingParams) => {
  const pageSize = requestParams.pageSize || PORTAL_COMPLIANCE_REGISTRY_ALL_ITEMS_PAGE_SIZE;
  const pageNumber = requestParams.pageNumber || 0;
  const startIndex = pageNumber * pageSize;
  const endIndex = startIndex + pageSize;
  const items = sortComplianceRegistryItemsForPortal(mapComplianceRegistryItemsToPortalData(complianceItems));

  return {
    result: items.slice(startIndex, endIndex),
    totalElements: items.length,
    totalPages: Math.ceil(items.length / pageSize),
  };
};

const useEmployeeComplianceRegistryService = () => {
  const employeeId = useEmployeeId();
  const { sendPostRequest } = usePeopleManagementApi();
  const { search: searchComplianceReport } = useComplianceReportService();
  const { searchBase } = useNewProjectService();
  const { getCurrentEmployee } = useEmployeeService();
  const { findAll: findAllOffices } = useOfficeService();
  const { findAll: findAllPositions } = usePositionService();

  const searchComplianceRegistryBase = async (requestParams: LoadingParams, includeSignatureFilter: boolean) => {
    const filter: Filter = {
      ...requestParams?.filter,
      alias: {
        eq: employeeId,
      },
    };

    if (includeSignatureFilter) {
      filter.isSignatureRequired = {
        eq: true,
      };
    }

    const response = await searchComplianceReport({
      ...requestParams,
      filter,
    });
    const result = response.result as ComplianceDataType[];
    const complianceItems = result[0]?.policies || [];

    return {
      result: complianceItems,
    };
  };

  const searchEmployeeComplianceRegistry = async (requestParams: LoadingParams) => {
    return searchComplianceRegistryBase(requestParams, true);
  };

  const searchEmployeePortalComplianceRegistry = async (
    requestParams: LoadingParams,
  ): Promise<ListResponse<PortalComplianceGroupedEntry>> => {
    const response = await searchComplianceRegistryBase(
      {
        ...requestParams,
        pageNumber: 0,
      },
      false,
    );
    const portalComplianceRegistry = transformPortalComplianceRegistry(response.result || [], requestParams);
    const [employee, offices, positions] = await Promise.all([
      getCurrentEmployee(),
      findAllOffices(),
      findAllPositions(),
    ]);
    const projects = await loadProjectsForPortalReasons(searchBase, portalComplianceRegistry.result || []);
    const employeeProjectIds = Array.from(
      new Set(
        projects
          .flatMap(project => [project.id, project.legacyId])
          .filter(value => value !== null)
          .map(String),
      ),
    );
    const dictionaries = buildAssignmentReasonDictionaries(offices || [], positions || [], projects);
    const employeeContext = buildEmployeeAssignmentContext(employee, employeeProjectIds);
    const groupedData = buildGroupedDataByReason(portalComplianceRegistry.result || [], dictionaries, employeeContext);
    const groupedEntries = Object.entries(groupedData).map(([key, items]) => ({
      key,
      items: items,
    }));

    return {
      pageNumber: 0,
      ...response,
      ...portalComplianceRegistry,
      result: groupedEntries,
    };
  };

  const signComplianceDocument = async (data: ComplianceDocumentConsentPayload) => {
    const request = await sendPostRequest('/policy/consent', data);

    return request;
  };

  return {
    signComplianceDocument,
    search: searchEmployeeComplianceRegistry,
    searchPortalCompliance: searchEmployeePortalComplianceRegistry,
  };
};

export default useEmployeeComplianceRegistryService;
